import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { Add, Delete, Edit, Search } from "@mui/icons-material";

import api from "../../../services/api";

const CoachTypeMaster = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // =========================================================
  // STATE
  // =========================================================

  const [coachTypes, setCoachTypes] = useState([]);

  const [basicCoachTypes, setBasicCoachTypes] = useState([]);

  const [structuralCoachTypes, setStructuralCoachTypes] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);

  const rowsPerPage = 10;

  const [dialogOpen, setDialogOpen] = useState(false);

  const [editId, setEditId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // =========================================================
  // FORM
  // =========================================================

  const [form, setForm] = useState({
    code: "",
    basic_coach_type_code: "",
    structural_coach_type_code: "",
    ac_nonac: "",
    status: "Active",
  });

  // =========================================================
  // MESSAGE
  // =========================================================

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // =========================================================
  // LOAD STRUCTURAL COACH TYPES
  // =========================================================

  const loadStructuralCoachTypes = async () => {
    try {
      const response = await api.get("/structural-coach-types");

      if (response.data.success) {
        setStructuralCoachTypes(response.data.data || []);





      } else {
        showMessage(
          response.data.message || "Unable to load Structural Coach Types",
          "error",
        );
      }
    } catch (error) {
      console.error("Load Structural Coach Types:", error);

      showMessage(
        error.response?.data?.message ||
          "Unable to load Structural Coach Types",
        "error",
      );
    }
  };

  // =========================================================
  // LOAD BASIC COACH TYPES
  // =========================================================

  const loadBasicCoachTypes = async () => {
    try {
      const response = await api.get("/basic-coach-types");

      if (response.data.success) {
        setBasicCoachTypes(response.data.data || []);


        
      } else {
        showMessage(
          response.data.message || "Unable to load Basic Coach Types",
          "error",
        );
      }
    } catch (error) {
      console.error("Load Basic Coach Types:", error);

      showMessage(
        error.response?.data?.message || "Unable to load Basic Coach Types",
        "error",
      );
    }
  };

  // =========================================================
  // LOAD COACH TYPE MASTER
  // =========================================================

  const loadCoachTypes = async () => {
    try {
      const response = await api.get("/coach-type-master");

      if (response.data.success) {
        setCoachTypes(response.data.data || []);
       // console.log(response.data.data);
      } else {
        showMessage(
          response.data.message || "Unable to load Coach Type Master",
          "error",
        );
      }
    } catch (error) {
      console.error("Load Coach Type Master:", error);

      showMessage(
        error.response?.data?.message || "Unable to load Coach Type Master",
        "error",
      );
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        await Promise.all([
          loadStructuralCoachTypes(),
          loadBasicCoachTypes(),
          loadCoachTypes(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredData = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return coachTypes;
    }

    return coachTypes.filter((item) => {
      return [
        item.code,

        item.basic_coach_type_code,

        item.basic_coach_type_name,

        item.structural_coach_type_code,

        item.structural_coach_type_name,

        item.ac_nonac,

        item.status,

        item.createdby,

        item.updatedby,
      ].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(value),
      );
    });
  }, [coachTypes, search]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }

    if (totalPages === 0 && page !== 1) {
      setPage(1);
    }
  }, [totalPages, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    setForm({
      code: "",
      basic_coach_type_code: "",
      structural_coach_type_code: "",
      ac_nonac: "",
      status: "Active",
    });

    setEditId(null);
  };

  // =========================================================
  // ADD
  // =========================================================

  const handleOpenAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  // =========================================================
  // CLOSE
  // =========================================================

  const handleCloseDialog = () => {
    if (loading) {
      return;
    }

    setDialogOpen(false);
    resetForm();
  };

  // =========================================================
  // BASIC COACH TYPE CHANGE
  //
  // AC/NONAC is automatically taken from
  // basic_coach_types.ac_nonac
  // =========================================================

  const handleBasicCoachTypeChange = (event) => {
  const value = event.target.value;

  const selectedBasic = basicCoachTypes.find(
    (item) =>
      String(item.code || "")
        .trim()
        .toUpperCase() ===
      String(value || "")
        .trim()
        .toUpperCase()
  );

  setForm((prev) => ({
    ...prev,
    basic_coach_type_code: value,
    ac_nonac: selectedBasic?.ac_nonac || "",
  }));
};
  // =========================================================
  // STRUCTURAL COACH TYPE CHANGE
  //
  // Completely independent from Basic Coach Type
  // =========================================================

  const handleStructuralCoachTypeChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,

      structural_coach_type_code: value,
    }));
  };

  // =========================================================
  // GENERAL FORM CHANGE
  // =========================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (item) => {
    setEditId(item.id);

    /*
     * AC/NONAC should preferably come from
     * current Basic Coach Type master.
     */

    const selectedBasic = basicCoachTypes.find(
      (basic) =>
        String(basic.code || "")
          .trim()
          .toUpperCase() ===
        String(item.basic_coach_type_code || "")
          .trim()
          .toUpperCase(),
    );

    setForm({
      code: item.code || "",

      basic_coach_type_code: item.basic_coach_type_code || "",

      structural_coach_type_code: item.structural_coach_type_code || "",

      ac_nonac: selectedBasic?.ac_nonac || item.ac_nonac || "",

      status:
        Number(item.status) === 1 || item.status === "Active"
          ? "Active"
          : "Inactive",
    });

    setDialogOpen(true);
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async () => {
    // -------------------------------------------------------
    // VALIDATION
    // -------------------------------------------------------

    if (!form.code.trim()) {
      showMessage("Coach Type Master Code is required", "error");
      return;
    }

    if (!form.basic_coach_type_code) {
      showMessage("Please select Basic Coach Type", "error");
      return;
    }

    if (!form.structural_coach_type_code) {
      showMessage("Please select Structural Coach Type", "error");
      return;
    }

    if (!form.ac_nonac) {
      showMessage(
        "AC/NONAC could not be determined from Basic Coach Type",
        "error",
      );
      return;
    }

    try {
      setLoading(true);

      // -----------------------------------------------------
      // PAYLOAD
      //
      // AC/NONAC is NOT sent as an independent user value.
      // It is already determined by Basic Coach Type.
      // -----------------------------------------------------

      const payload = {
        code: form.code.trim().toUpperCase(),

        basic_coach_type_code: form.basic_coach_type_code.trim().toUpperCase(),

        structural_coach_type_code: form.structural_coach_type_code
          .trim()
          .toUpperCase(),

        status: form.status === "Active" ? 1 : 0,
      };

      // -----------------------------------------------------
      // UPDATE
      // -----------------------------------------------------

      if (editId !== null) {
        const response = await api.put(`/coach-type-master/${editId}`, payload);

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Coach Type Master update failed",
          );
        }

        showMessage("Coach Type Master updated successfully");
      }

      // -----------------------------------------------------
      // CREATE
      // -----------------------------------------------------
      else {
        const response = await api.post("/coach-type-master", payload);

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Coach Type Master creation failed",
          );
        }

        showMessage("Coach Type Master added successfully");
      }

      await loadCoachTypes();

      handleCloseDialog();
    } catch (error) {
      console.error("Save Coach Type Master:", error);

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to save Coach Type Master",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this Coach Type Master?",
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      const response = await api.delete(`/coach-type-master/${id}`);

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Coach Type Master deletion failed",
        );
      }

      await loadCoachTypes();

      showMessage("Coach Type Master deleted successfully");
    } catch (error) {
      console.error("Delete Coach Type Master:", error);

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to delete Coach Type Master",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // STATUS HELPER
  // =========================================================

  const isActive = (status) => {
    return Number(status) === 1 || status === "Active";
  };

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* =====================================================
          MAIN CARD
      ===================================================== */}

      <Card
        elevation={0}
        sx={{
          width: "100%",
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* ===================================================
            TOOLBAR
        =================================================== */}

        <Box
          sx={{
            p: 2,

            display: "flex",

            gap: 2,

            justifyContent: "space-between",

            alignItems: {
              xs: "stretch",
              sm: "center",
            },

            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <TextField
            size="small"
            placeholder="Search Coach Type Master..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: {
                xs: "100%",
                sm: 380,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{
              xs: "space-between",
              sm: "flex-end",
            }}
          >
            <Chip label={`${filteredData.length} Records`} variant="outlined" />

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                whiteSpace: "nowrap",
              }}
            >
              Add Coach Type
            </Button>
          </Stack>
        </Box>

        {/* ===================================================
            DESKTOP TABLE
        =================================================== */}

        {!isMobile ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1050,
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    #
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Coach Type Code
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Basic Coach Type
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    AC / NONAC
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Structural Coach Type
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Created By
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Updated By
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>

                    {/* CODE */}

                    <TableCell>
                      <Typography fontWeight={800}>{item.code}</Typography>
                    </TableCell>

                    {/* BASIC */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.basic_coach_type_code || "-"}

                        {item.basic_coach_type_name
                          ? ` - ${item.basic_coach_type_name}`
                          : ""}
                      </Typography>
                    </TableCell>

                    {/* AC NONAC */}

                    <TableCell>
                      <Chip
                        label={item.basic_ac_nonac  || "-"}
                        size="small"
                        color={item.basic_ac_nonac  === "AC" ? "primary" : "default"}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* STRUCTURAL */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.structural_coach_type_code || "-"}

                        {item.structural_coach_type_name
                          ? ` - ${item.structural_coach_type_name}`
                          : ""}
                      </Typography>
                    </TableCell>

                    {/* STATUS */}

                    <TableCell>
                      <Chip
                        label={isActive(item.status) ? "Active" : "Inactive"}
                        size="small"
                        color={isActive(item.status) ? "success" : "default"}
                      />
                    </TableCell>

                    {/* CREATED */}

                    <TableCell>{item.createdby || "-"}</TableCell>

                    {/* UPDATED */}

                    <TableCell>{item.updatedby || "-"}</TableCell>

                    {/* ACTIONS */}

                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{
                        py: 5,
                      }}
                    >
                      <Typography color="text.secondary">
                        {loading
                          ? "Loading..."
                          : "No Coach Type Master records found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* =================================================
             MOBILE CARDS
          ================================================= */

          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#f8fafc",
            }}
          >
            {paginatedData.map((item, index) => (
              <Card
                key={item.id}
                elevation={0}
                sx={{
                  mb: 1.5,
                  border: "1px solid #e2e8f0",
                  borderRadius: 2.5,
                }}
              >
                <CardContent>
                  {/* HEADER */}

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        #{(page - 1) * rowsPerPage + index + 1}
                      </Typography>

                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          wordBreak: "break-word",
                        }}
                      >
                        {item.code}
                      </Typography>
                    </Box>

                    <Chip
                      label={isActive(item.status) ? "Active" : "Inactive"}
                      size="small"
                      color={isActive(item.status) ? "success" : "default"}
                    />
                  </Stack>

                  <Divider
                    sx={{
                      my: 1.5,
                    }}
                  />

                  {/* BASIC */}

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.8,
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Basic Coach Type:</strong>{" "}
                    {item.basic_coach_type_code || "-"}
                    {item.basic_coach_type_name
                      ? ` - ${item.basic_coach_type_name}`
                      : ""}
                  </Typography>

                  {/* AC NONAC */}

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.8,
                    }}
                  >
                    <strong>AC / NONAC:</strong>{" "}
                    <Chip
                      label={item.basic_ac_nonac  || "-"}
                      size="small"
                      color={item.basic_ac_nonac  === "AC" ? "primary" : "default"}
                      variant="outlined"
                      sx={{
                        ml: 0.5,
                      }}
                    />
                  </Typography>

                  {/* STRUCTURAL */}

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.8,
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Structural Coach Type:</strong>{" "}
                    {item.structural_coach_type_code || "-"}
                    {item.structural_coach_type_name
                      ? ` - ${item.structural_coach_type_name}`
                      : ""}
                  </Typography>

                  {/* CREATED */}

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Created By:</strong> {item.createdby || "-"}
                  </Typography>

                  {/* UPDATED */}

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Updated By:</strong> {item.updatedby || "-"}
                  </Typography>

                  {/* ACTIONS */}

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleEdit(item)}
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(item.id)}
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            {paginatedData.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 5,
                }}
              >
                <Typography color="text.secondary">
                  {loading
                    ? "Loading..."
                    : "No Coach Type Master records found"}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ===================================================
            PAGINATION
        =================================================== */}

        {totalPages > 1 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* =====================================================
          ADD / EDIT DIALOG
      ===================================================== */}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          {editId !== null ? "Edit Coach Type Master" : "Add Coach Type Master"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            spacing={2.2}
            sx={{
              pt: 1,
            }}
          >
            {/* =================================================
                OWN CODE
            ================================================= */}

            <TextField
              fullWidth
              label="Coach Type Master Code"
              name="code"
              value={form.code}
              onChange={handleChange}
              required
              placeholder="Enter Coach Type Code"
            />

            {/* =================================================
                BASIC COACH TYPE
            ================================================= */}

            <TextField
              select
              fullWidth
              label="Basic Coach Type"
              value={form.basic_coach_type_code}
              onChange={handleBasicCoachTypeChange}
              required
            >
              <MenuItem value="">Select Basic Coach Type</MenuItem>

              {basicCoachTypes
                .filter(
                  (item) =>
                    Number(item.status) === 1 || item.status === "Active",
                )
                .map((item) => (
                  <MenuItem key={item.id} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
            </TextField>

            {/* =================================================
                AC / NONAC
            ================================================= */}

            <TextField
              fullWidth
              label="AC / NONAC"
              value={form.ac_nonac || ""}
              disabled
              helperText={
                form.ac_nonac
                  ? "Automatically determined from Basic Coach Type"
                  : "Select Basic Coach Type first"
              }
            />

            {/* =================================================
                STRUCTURAL COACH TYPE
            ================================================= */}

            <TextField
              select
              fullWidth
              label="Structural Coach Type"
              value={form.structural_coach_type_code}
              onChange={handleStructuralCoachTypeChange}
              required
            >
              <MenuItem value="">Select Structural Coach Type</MenuItem>

              {structuralCoachTypes
                .filter(
                  (item) =>
                    Number(item.status) === 1 || item.status === "Active",
                )
                .map((item) => (
                  <MenuItem key={item.id} value={item.code}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
            </TextField>

            {/* =================================================
                STATUS
            ================================================= */}

            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <MenuItem value="Active">Active</MenuItem>

              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            disabled={loading}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            {loading ? "Saving..." : editId !== null ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoachTypeMaster;
